/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Button, Text } from '@/shared/ui';

interface FilterChipsProps {
  activeFilters: string[];
  onRemoveFilter: (filter: string) => void;
  onClearAllFilters: () => void;
}

/**
 * Renders the active filter chips row with per-chip removal and a clear-all action.
 */
export const PageHeaderFilterChips: React.FC<FilterChipsProps> = ({ activeFilters, onRemoveFilter, onClearAllFilters }) => {
  const { t } = useTranslation('features/properties');
  if (activeFilters.length === 0) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
      {activeFilters.map((filter) => (
        <Text
          as="span"
          key={filter}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            padding: 'var(--space-1) var(--space-3)',
            borderRadius: 'var(--radius-full)',
            background: 'color-mix(in srgb, var(--seed-base) 12%, var(--surface-primary))',
            border: '1px solid color-mix(in srgb, var(--seed-base) 30%, transparent)',
            color: 'var(--seed-base)',
            fontSize: 'var(--text-meta)',
            fontWeight: 'var(--font-medium)' as unknown as number,
          }}
        >
          {filter}
          <Button
            type="button"
            data-testid={`remove-filter-${filter}`}
            onClick={() => onRemoveFilter(filter)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--seed-base)',
              opacity: 0.7,
              padding: 0,
            }}
          >
            <Cross2Icon width={10} height={10} />
          </Button>
        </Text>
      ))}
      <Button
        type="button"
        onClick={onClearAllFilters}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--seed-base)',
          fontSize: 'var(--text-meta)',
          fontWeight: 'var(--font-medium)' as unknown as number,
          cursor: 'pointer',
          textDecoration: 'underline',
          fontFamily: 'var(--font-family)',
        }}
      >
        {t('listHeader.clearAll')}
      </Button>
    </div>
  );
};
